import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { Platform, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'utils/packages/linearGradient';
import Animated, { Extrapolation, interpolate, interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, NavigationHeader, Spacer } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconCircle from 'design-system/icons/IconCircle';
import IconPlus from 'design-system/icons/IconPlus';
import Text, { lineHeight } from 'design-system/Text';
import { hitSlopS, rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld, useHeaderHeight } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import useColorScheme from 'hooks/useColorScheme';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import createStyleSheets from 'utils/createStyleSheets';
import { goBackToTopWithStops } from 'utils/navigationv6';
import Amplitude from 'utils/amplitude';
import AllocationFundCard from '../components/AllocationFundCard';
import { editAutoInvestPlan, resumeAutoInvestPlan } from '../actions/autoinvest';
import { activateFundPicker, deactivateFundPicker, getSnapshot } from '../hooks/useFundPickerStore';
import { isWeb } from '../../../constants';
const EMPTY_FUNDS = [];
const REMAINING_ROW_HEIGHT = rem(56);
const AutoinvestAllocationScreen = ({ route, navigation }) => {
    const colors = useColors();
    const colorScheme = useColorScheme();
    const styles = useStyles(styleSet);
    const headerHeight = useHeaderHeight();
    const { paddingHorizontal, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const isEditMode = route.params.isEditMode ?? false;
    const wasPaused = route.params.wasPaused ?? false;
    const { planId } = route.params;
    const [, , loadingAction, fetch] = useFetchOld(muteErrorAndResult);
    const initialAllocs = route.params.initialAllocations;
    const [funds, setFunds] = useState(() => (initialAllocs?.length ? initialAllocs : EMPTY_FUNDS));
    const [isDragging, setIsDragging] = useState(false);
    const initialFundsRef = useRef(funds);
    const isUpsellFlow = route.params.isUpsellFlow ?? false;
    const scrollY = useSharedValue(0);
    const [headerInfoHeight, setHeaderInfoHeight] = useState(0);
    const headerInfoHeightShared = useSharedValue(0);
    const linesRef = useRef({});
    const headerInputRange = useMemo(() => [0, headerInfoHeight], [headerInfoHeight]);
    useLayoutEffect(() => {
        navigation.setOptions({
            fullScreenGestureEnabled: false,
            headerTransparent: true,
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        Amplitude.logEvent('Set Allocation', { type: 'autoinvest' });
    }, []);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const totalAllocated = useMemo(() => Math.round(funds.reduce((sum, f) => sum + f.percentage, 0)), [funds]);
    const hasChanged = useMemo(() => {
        const initial = initialFundsRef.current;
        const nonZeroFunds = funds.filter((f) => f.percentage > 0);
        if (nonZeroFunds.length !== initial.length)
            return true;
        return nonZeroFunds.some((f) => initial.find((i) => i.assetId === f.assetId)?.percentage !== f.percentage);
    }, [funds]);
    const handlePercentageChange = useCallback((assetId, value) => {
        setFunds((prev) => {
            const current = prev.find((f) => f.assetId === assetId);
            if (!current)
                return prev;
            // 2 funds: linked — changing one adjusts the other (only when already at 100%)
            const currentTotal = Math.round(prev.reduce((sum, f) => sum + f.percentage, 0));
            if (prev.length === 2 && currentTotal === 100) {
                const clamped = Math.round(Math.min(value, 100));
                return prev.map((f) => f.assetId === assetId ? { ...f, percentage: clamped } : { ...f, percentage: 100 - clamped });
            }
            // 1 or 3+ funds: clamp so total never exceeds 100
            const othersTotal = prev.reduce((sum, f) => (f.assetId === assetId ? sum : sum + f.percentage), 0);
            const clamped = Math.round(Math.min(value, 100 - othersTotal));
            if (clamped === current.percentage)
                return prev;
            return prev.map((f) => (f.assetId === assetId ? { ...f, percentage: clamped } : f));
        });
    }, []);
    const handleDelete = useCallback((assetId) => {
        setFunds((prev) => prev.filter((f) => f.assetId !== assetId));
    }, []);
    const handleClear = useCallback(() => {
        setFunds((prev) => prev.map((f) => ({ ...f, percentage: 0 })));
    }, []);
    useFocusEffect(useCallback(() => {
        const snapshot = getSnapshot();
        if (!snapshot.active || !snapshot.saved) {
            if (snapshot.active)
                deactivateFundPicker();
            return;
        }
        const picked = snapshot.selectedFunds;
        setFunds((prev) => {
            const merged = picked.map((p) => {
                const existing = prev.find((f) => f.assetId === p);
                return {
                    assetId: p,
                    percentage: existing?.percentage ?? 0,
                };
            });
            const hasAllocation = merged.some((f) => f.percentage > 0);
            if (!hasAllocation && merged.length > 0) {
                const rawEach = Math.floor(100 / merged.length);
                const stepped = Math.floor(rawEach / 5) * 5;
                const remainder = 100 - stepped * merged.length;
                return merged.map((f, i) => ({ ...f, percentage: stepped + (i === 0 ? remainder : 0) }));
            }
            return merged;
        });
        deactivateFundPicker();
    }, []));
    const handleAddFunds = useCallback(() => {
        activateFundPicker(funds.map((f) => f.assetId));
        navigation.navigate('InvestDiscover', { start: 'readyMade' });
    }, [funds, navigation]);
    const handleContinue = useCallback(async () => {
        const allocations = [];
        funds.forEach((f) => {
            if (f.percentage !== 0) {
                allocations.push({ assetId: f.assetId, percentage: f.percentage / 100 });
            }
        });
        if (isEditMode && planId) {
            const result = await fetch(editAutoInvestPlan(planId, { allocations }));
            if (result && !('error' in result)) {
                if (isUpsellFlow) {
                    if (wasPaused) {
                        const resumeResult = await fetch(resumeAutoInvestPlan(planId));
                        if (!resumeResult || 'error' in resumeResult)
                            return;
                    }
                    navigation.navigateDeprecated('Success', {
                        title: wasPaused ? 'Autoinvest resumed' : 'Fund added to Autoinvest',
                        subTitle: wasPaused
                            ? 'Your monthly investments are active. You can pause at any time.'
                            : "It's now included in your monthly allocation. You can edit your allocation at any time.",
                        buttonText: 'Done',
                        onContinue: () => {
                            goBackToTopWithStops(navigation, ['InvestPortfolio']);
                            navigation.navigate('AutoinvestManage', { accountType: route.params.accountType });
                            return true;
                        },
                    });
                }
                else {
                    navigation.goBack();
                }
            }
            return;
        }
        navigation.navigate('AutoinvestSelectFundingAccount', {
            accountType: route.params.accountType,
            collectionDate: route.params.collectionDate,
            amount: route.params.amount,
            allocations,
        });
    }, [
        funds,
        isEditMode,
        planId,
        route.params.accountType,
        route.params.collectionDate,
        route.params.amount,
        isUpsellFlow,
        wasPaused,
    ]);
    const updatedMeasurement = useCallback(() => {
        const measurements = linesRef.current;
        if (measurements.title !== undefined && measurements.subTitle !== undefined) {
            const height = rem(16 + 4 + measurements.title * lineHeight.Text_24 + measurements.subTitle * lineHeight.TextThin_16);
            headerInfoHeightShared.value = height;
            setHeaderInfoHeight(height);
        }
    }, [headerInfoHeightShared]);
    const onTextLayoutTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.title === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            title: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const onTextLayoutSubTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.subTitle === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            subTitle: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const stickyTranslateY = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(scrollY.value, [0, headerInfoHeightShared.value, headerInfoHeightShared.value + 1], [0, -headerInfoHeightShared.value, -headerInfoHeightShared.value]),
            },
        ],
    }));
    const bgStart = colors.background.dark;
    const bgDocked = colors.cards.onDark;
    const stickyCardAnimatedStyle = useAnimatedStyle(() => {
        if (headerInfoHeightShared.value === 0)
            return {};
        return {
            shadowOpacity: colorScheme === 'dark'
                ? 0
                : interpolate(scrollY.value, [headerInfoHeightShared.value - 20, headerInfoHeightShared.value], [0, 1]),
            backgroundColor: interpolateColor(scrollY.value, [headerInfoHeightShared.value - 20, headerInfoHeightShared.value], [bgStart, bgDocked]),
        };
    });
    const androidShadowOpacity = useAnimatedStyle(() => {
        if (headerInfoHeightShared.value === 0)
            return {};
        return {
            opacity: interpolate(scrollY.value, [headerInfoHeightShared.value - 20, headerInfoHeightShared.value], [0, 1], Extrapolation.CLAMP),
        };
    });
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader hasShadow={false} scrollY={scrollY} bgColorInputRange={headerInputRange} shadowInputRange={headerInputRange} withFrame/>
      <Animated.ScrollView scrollEnabled={!isDragging} scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <View style={styles.container}>
          <Text Text-24 onTextLayout={onTextLayoutTitle}>
            {isEditMode ? 'Edit your allocation' : 'Set your allocation'}
          </Text>
          <Spacer h={4}/>
          <Text TextThin-16 Secondary onTextLayout={onTextLayoutSubTitle}>
            This will be your split for future Autoinvests. You can change it anytime
          </Text>
        </View>

        {headerInfoHeight ? (<Spacer h={REMAINING_ROW_HEIGHT}/>) : (<View style={styles.remainingRowInline}>
            <Text Text-16>Remaining: {100 - totalAllocated}%</Text>
            {totalAllocated > 0 && (<TouchableOpacity onPress={handleClear}>
                <Text Text-14 Primary>
                  Clear
                </Text>
              </TouchableOpacity>)}
          </View>)}
        <CardView noMarginTop>
          <TouchableOpacity onPress={handleAddFunds} style={styles.addFundContent}>
            <IconCircle width={44} height={44}>
              <IconPlus color={colors.text.brand}/>
            </IconCircle>
            <Spacer w={16}/>
            <Text Text-16>Add funds or ETFs</Text>
          </TouchableOpacity>
        </CardView>
        {funds.map((fund) => (<AllocationFundCard key={fund.assetId} assetId={fund.assetId} percentage={fund.percentage} maximumValue={funds.length === 2 ? 100 : fund.percentage + (100 - totalAllocated)} onPercentageChange={(value) => handlePercentageChange(fund.assetId, value)} onDelete={() => handleDelete(fund.assetId)} onSlidingStart={() => setIsDragging(true)} onSlidingComplete={() => setIsDragging(false)}/>))}
      </Animated.ScrollView>
      {!!headerInfoHeight && (<Animated.View style={[styles.stickyContainer, stickyTranslateY, { marginTop: headerHeight + headerInfoHeight }]}>
          <Animated.View style={[styles.stickyCard, stickyCardAnimatedStyle, { paddingHorizontal: paddingHorizontal + rem(16) }]}>
            <Text Text-16>
              Remaining: <Text Numbers-16>{100 - totalAllocated}%</Text>
            </Text>
            {totalAllocated > 0 && (<TouchableOpacity hitSlop={hitSlopS} onPress={handleClear}>
                <Text Text-14 Primary>
                  Clear
                </Text>
              </TouchableOpacity>)}
          </Animated.View>
          {Platform.OS === 'android' && (<Animated.View style={androidShadowOpacity}>
              <LinearGradient pointerEvents="none" style={styles.androidShadow} colors={[
                    colors.shadows.androidShadowGradientStart,
                    colors.shadows.androidShadowGradientEnd,
                ]}/>
            </Animated.View>)}
        </Animated.View>)}
      <ButtonGroup type="floating">
        <Button brand title={isEditMode && !isUpsellFlow ? 'Save' : 'Continue'} onPress={handleContinue} disabled={totalAllocated !== 100 || (isEditMode && !hasChanged)} loading={loadingAction}/>
      </ButtonGroup>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    remainingRowInline: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: rem(16),
        paddingTop: 0,
    },
    stickyContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 1,
    },
    stickyCard: {
        paddingVertical: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...colors.shadows.extendedHeader,
    },
    androidShadow: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 10,
    },
    addFundContent: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
}));
export default AutoinvestAllocationScreen;
